import { Check } from "lucide-react";
import { PRODUCTS, SIZES, ADDONS } from "@/data/pricing";

export const OptionGrid = ({ options, value, onChange, cols = "sm:grid-cols-3", testPrefix, multi = false }) => (
  <div className={`grid grid-cols-1 ${cols} gap-3`} data-testid={`${testPrefix}-options`}>
    {options.map((o) => {
      const selected = multi ? value.includes(o.name) : value === o.name;
      return (
        <button key={o.name} type="button" onClick={() => onChange(o.name)} data-testid={`${testPrefix}-option-${o.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
          aria-pressed={selected}
          className={`group relative text-left p-5 border transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${selected ? "border-brass bg-brass/10" : "border-line-dark bg-ink-3 hover:border-stone"}`}>
          <span className={`absolute top-4 right-4 h-5 w-5 rounded-full border flex items-center justify-center transition-colors ${selected ? "bg-brass border-brass text-ink" : "border-stone/50 text-transparent"}`}><Check size={12} strokeWidth={3} /></span>
          <span className="block font-serif text-xl text-linen pr-6">{o.name}</span>
          {(o.note || o.desc) && <span className="block mt-1 text-xs text-linen/50">{o.note || o.desc}</span>}
          {o.add > 0 && <span className="block mt-2 text-[11px] font-mono text-brass">+A${o.add} / window</span>}
          {o.perWindow && <span className="block mt-2 text-[11px] font-mono text-brass">A${o.perWindow} / window</span>}
        </button>
      );
    })}
  </div>
);

export const StepProduct = ({ s, set }) => (
  <OptionGrid testPrefix="product" options={Object.entries(PRODUCTS).map(([name, p]) => ({ name, note: p.blurb }))} value={s.product}
    onChange={(v) => set({ product: v, style: "", hardware: "", tier: "", fn: "" })} />
);

export const StepStyle = ({ s, set }) => {
  const p = PRODUCTS[s.product];
  return (
    <div className="space-y-8">
      <div><p className="label">Style</p><OptionGrid testPrefix="style" options={p.styles} value={s.style} onChange={(v) => set({ style: v })} cols="sm:grid-cols-2 lg:grid-cols-4" /></div>
      <div><p className="label">Track / Hardware</p><OptionGrid testPrefix="hardware" options={p.hardware} value={s.hardware} onChange={(v) => set({ hardware: v })} /></div>
    </div>
  );
};

export const StepFabric = ({ s, set }) => {
  const p = PRODUCTS[s.product];
  return (
    <div className="space-y-8">
      <div><p className="label">{p.tierLabel}</p><OptionGrid testPrefix="tier" options={p.tiers} value={s.tier} onChange={(v) => set({ tier: v })} /></div>
      <div><p className="label">{p.fnLabel}</p><OptionGrid testPrefix="function" options={p.functions} value={s.fn} onChange={(v) => set({ fn: v })} /></div>
    </div>
  );
};

export const StepWindows = ({ s, set }) => (
  <div className="space-y-8">
    <div>
      <p className="label">Number of windows</p>
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => set({ windows: Math.max(1, s.windows - 1) })} className="h-12 w-12 border border-line-dark text-linen text-xl hover:border-brass transition-colors" data-testid="windows-decrement-btn">−</button>
        <input type="number" min={1} max={50} value={s.windows} onChange={(e) => set({ windows: Math.min(50, Math.max(1, Number(e.target.value) || 1)) })}
          className="field-dark w-24 text-center font-serif text-2xl" data-testid="windows-input" />
        <button type="button" onClick={() => set({ windows: Math.min(50, s.windows + 1) })} className="h-12 w-12 border border-line-dark text-linen text-xl hover:border-brass transition-colors" data-testid="windows-increment-btn">+</button>
        <input type="range" min={1} max={20} value={Math.min(20, s.windows)} onChange={(e) => set({ windows: Number(e.target.value) })} className="flex-1 accent-brass hidden sm:block" data-testid="windows-slider" />
      </div>
    </div>
    <div><p className="label">Approximate size (per window)</p><OptionGrid testPrefix="size" options={SIZES} value={s.size} onChange={(v) => set({ size: v })} cols="sm:grid-cols-2 lg:grid-cols-4" /></div>
  </div>
);

export const StepAddons = ({ s, set }) => (
  <div>
    <p className="label">Optional services (select any)</p>
    <OptionGrid testPrefix="addon" multi options={ADDONS} value={s.addons} cols="sm:grid-cols-2"
      onChange={(v) => set({ addons: s.addons.includes(v) ? s.addons.filter((a) => a !== v) : [...s.addons, v] })} />
    <p className="mt-4 text-xs text-linen/40">Skip installation if you're a confident DIYer — we'll still supply fixings and instructions.</p>
  </div>
);
